// src/components/sections/ProjectLinks.jsx - New component
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button
} from '@mui/material';
import TeamsIcon from '@mui/icons-material/People';
import SummaryIcon from '@mui/icons-material/Description';
import ContractIcon from '@mui/icons-material/RequestQuote';
import { apiFetch } from '../../utils/api';
import { Logger } from '../../utils/logger';

const logger = new Logger('ProjectLinks');

/**
 * ProjectLinks component displays links to important project resources
 */
const ProjectLinks = ({
  projectId,
  teamsUrl,
  summaryUrl,
  sowUrl
}) => {
  /**
   * Handle Statement of Work download with authentication
   */
  const handleSowDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!projectId) {
      logger.error('No project ID provided for SOW download');
      return;
    }

    try {
      logger.debug(`Fetching SOW for project ${projectId}`);

      // Format the ID to ensure it starts with 'P'
      const formattedId = projectId.toString().startsWith('P')
        ? projectId
        : `P${projectId}`;

      // Fetch the PDF with authentication
      const response = await apiFetch(`projects/${formattedId}/sow`, {
        method: 'GET'
      });

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn(`SOW not found for project ${formattedId}`);
          alert('Statement of Work not found for this project');
          return;
        }
        throw new Error(`HTTP error ${response.status}`);
      }

      // Get the PDF blob and open in new tab
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after a short delay to allow the new tab to load
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      logger.debug(`SOW opened in new tab for project ${formattedId}`);
    } catch (error) {
      logger.error('Error downloading SOW:', error);
      alert('Failed to download Statement of Work. Please try again.');
    }
  };

  return (
    <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 1 }}>
        PROJECT RESOURCES
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TeamsIcon fontSize="small" />}
          disabled
          sx={{
            fontSize: '0.65rem',
            borderRadius: 1,
            flex: 1,
            textTransform: 'none'
          }}
        >
          MS Teams Site
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<SummaryIcon fontSize="small" />}
          disabled
          sx={{
            fontSize: '0.65rem',
            borderRadius: 1,
            flex: 1,
            textTransform: 'none'
          }}
        >
          Project Summary
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<ContractIcon fontSize="small" />}
          disabled
          sx={{
            fontSize: '0.65rem',
            borderRadius: 1,
            flex: 1,
            textTransform: 'none'
          }}
        >
          Statement of Work
        </Button>
      </Box>
    </Box>
  );
};

ProjectLinks.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  teamsUrl: PropTypes.string,
  summaryUrl: PropTypes.string,
  sowUrl: PropTypes.string
};

export default ProjectLinks;
